import { useAuth } from '@cibo/profile'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buffer } from '@turf/turf'
import { AxiosError } from 'axios'
import { Feature } from 'geojson'
import { assocPath } from 'ramda'
import { logRequestError } from '../../consts/Theme'
import { AGREEMENTS_QUERY_KEY } from '../Agreements'
import {
  BulkConfirmRequest,
  ControlGroup,
  ControlGroupSearchQuery,
  ControlGroupsAPI,
} from './ControlGroupsAPI'

// expressed in km
const METER_BUFFER = 1 / 1000

export const CONTROL_GROUPS_QUERY_KEY = {
  CONTROL_GROUP: 'control_group',
  CONTROL_GROUPS_SEARCH: 'control_groups_search',
}

export const useControlGroup = (termsetId: string | undefined) => {
  const { isLoggedIn } = useAuth()
  return useQuery(
    [CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUP, termsetId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => ControlGroupsAPI.retrieve(termsetId!).then(response => response?.item),
    {
      enabled: isLoggedIn && !!termsetId,
    }
  )
}

export const useAllControlGroups = () => {
  const { isLoggedIn } = useAuth()
  return useQuery(
    [CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUPS_SEARCH, 'all'],
    async () => {
      const groups: ControlGroup[] = []
      const response = await ControlGroupsAPI.search({ offset: 0, limit: 1000 })

      const numAvailable = response.numAvailable
      response.items.forEach(item => groups.push(item))

      const numPages = Math.ceil(numAvailable / 1000)

      if (numPages > 1) {
        const responses = await Promise.all(
          Array(numPages - 1)
            .fill(0)
            .map((_, index) => ControlGroupsAPI.search({ offset: (index + 1) * 1000, limit: 1000 }))
        )

        responses.forEach(response => response.items.forEach(item => groups.push(item)))
      }

      return groups
    },
    {
      enabled: isLoggedIn,
    }
  )
}

export const usePaginatedControlGroups = (query?: ControlGroupSearchQuery, options?: any) => {
  const { isLoggedIn } = useAuth()
  return useQuery(
    [CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUPS_SEARCH, query],
    () => ControlGroupsAPI.search(query),
    {
      enabled: isLoggedIn,
      ...options,
    }
  )
}

export const useUpdateControlGroup = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ controlGroupId, feature }: { controlGroupId: string; feature?: Feature }) => {
      let error

      if (!feature) {
        return ControlGroupsAPI.update({
          id: controlGroupId,
          details: [{ traitId: 'geometry' }],
        }).catch(e => {
          logRequestError(e)
          throw e
        })
      }

      const firstTryFeature = assocPath(['properties', 'attempt'], 'no_buffer', feature)
      firstTryFeature.id = feature.id

      const firstTry = await ControlGroupsAPI.update({
        id: controlGroupId,
        details: [
          {
            traitId: 'geometry',
            input: { feature: firstTryFeature },
          },
        ],
      }).catch(e => {
        logRequestError(e)
        error = e
      })

      if (!error && firstTry?.item?.status === 'valid') {
        return firstTry
      }

      error = undefined

      const secondTryFeature = assocPath(
        ['properties', 'attempt'],
        'outset',
        buffer(feature, METER_BUFFER)
      )
      secondTryFeature.id = feature.id

      const secondTry = await ControlGroupsAPI.update({
        id: controlGroupId,
        details: [
          {
            traitId: 'geometry',
            input: {
              feature: secondTryFeature,
            },
          },
        ],
      }).catch(e => {
        logRequestError(e)
        error = e
      })

      if (!error && secondTry?.item?.status === 'valid') {
        return secondTry
      }

      // can not use negative buffer whole whole-field plots
      // because the "creditable" field will be an unusably thin ring
      if (
        feature.properties?.plotType === 'wholeField' ||
        feature.properties?.plotType === 'smallField'
      ) {
        if (error) {
          throw error
        }
        return secondTry
      }

      error = undefined

      const thirdTryFeature = assocPath(
        ['properties', 'attempt'],
        'inset',
        buffer(feature, -METER_BUFFER)
      )
      thirdTryFeature.id = feature.id

      const thirdTry = await ControlGroupsAPI.update({
        id: controlGroupId,
        details: [
          {
            traitId: 'geometry',
            input: {
              feature: thirdTryFeature,
            },
          },
        ],
      }).catch(e => {
        logRequestError(e)
        error = e
      })

      if (error) {
        throw error
      }

      return thirdTry
    },
    {
      onSuccess: args => {
        queryClient.setQueryData(
          [CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUP, args?.item.id],
          args?.item
        )
        queryClient.invalidateQueries([CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUPS_SEARCH])
      },
    }
  )
}

export const useBulkConfirmControlGroups = () => {
  const queryClient = useQueryClient()

  return useMutation((request: BulkConfirmRequest) => ControlGroupsAPI.bulkConfirm(request), {
    onSuccess: (_, request) => {
      queryClient.invalidateQueries([CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUP])
      queryClient.invalidateQueries([CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUPS_SEARCH])
      queryClient.invalidateQueries([AGREEMENTS_QUERY_KEY.AGREEMENTS, request.agreementId])
    },
    onError: (error: AxiosError) => {
      logRequestError(error)
    },
  })
}

// @TODO - find these by engagmentId as in CPD-3458
export const useControlGroupsForAgreement = (agreementId?: string) => {
  const controlGroupsForAgreement = usePaginatedControlGroups(
    agreementId ? { agreementId } : undefined,
    { enabled: !!agreementId }
  )

  return { ...controlGroupsForAgreement, data: controlGroupsForAgreement.data?.items }
}
